/**
 * Assemble an absolute (or relative) Assets URL given the resolved host.
 *
 * - `host === null` (same-site)            → returns the relative `path`
 * - `host === 'foo.atlassian.net'`         → returns `https://foo.atlassian.net${path}`
 * - `host === 'localhost:8080'` + opts     → returns `http://localhost:8080${path}` when
 *                                            `protocol: 'http'` is passed (test/dev only)
 *
 * The `relativePath` must start with `/jira/assets/...`. This is enforced at
 * runtime (see the guard below) rather than at the type level so that callers
 * passing a router-derived `string` don't have to cast every time. A
 * mismatched prefix throws synchronously, surfacing the bug immediately.
 *
 * `protocol` defaults to `'https'` and is **only** intended as an escape hatch
 * for localhost / `*.jira-dev.com` test environments. Production call-sites
 * should never pass it.
 */
export type BuildAbsoluteAssetsUrlOptions = {
	readonly protocol?: 'https' | 'http';
};

export const buildAbsoluteAssetsUrl = (
	host: string | null,
	relativePath: string,
	options: BuildAbsoluteAssetsUrlOptions = {},
): string => {
	if (host === null || host === '') {
		return relativePath;
	}
	// Strip any accidental scheme the caller may have included so we never
	// produce a `https://https://...` URL when the caller is lazy.
	const normalisedHost = host.replace(/^https?:\/\//, '');
	const protocol = options.protocol ?? 'https';
	return `${protocol}://${normalisedHost}${relativePath}`;
};
